/**
 * OCR Scanner Component - Scant tekst van camera of foto
 * Gebruikt Tesseract.js voor tekstherkenning
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import Tesseract, { LoggerMessage, Worker } from 'tesseract.js';
import './OCRScanner.css';

interface OCRScannerProps {
  onTextScanned: (text: string) => void;
  onClose: () => void;
}

const OCRScanner = ({ onTextScanned, onClose }: OCRScannerProps) => {
  const [scanning, setScanning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [scannedText, setScannedText] = useState<string | null>(null);
  const [torchOn, setTorchOn] = useState(false);
  const [torchAvailable, setTorchAvailable] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const trackRef = useRef<MediaStreamTrack | null>(null);
  const workerRef = useRef<Worker | null>(null);

  // Zoek camera met torch door alle camera's te proberen
  const findCameraWithTorch = async (): Promise<{stream: MediaStream, track: MediaStreamTrack} | null> => {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const cameras = devices.filter(d => d.kind === 'videoinput');

    // Probeer elke camera (vaak is de laatste degene met torch)
    for (const cam of cameras.reverse()) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: { exact: cam.deviceId } }
        });
        const track = stream.getVideoTracks()[0];
        const capabilities = track.getCapabilities() as MediaTrackCapabilities & { torch?: boolean };

        if (capabilities.torch === true) {
          return { stream, track };
        } else {
          track.stop();
        }
      } catch (e) {
        console.log('Camera skip:', cam.deviceId);
      }
    }
    return null;
  };

  // Start camera
  const startCamera = useCallback(async () => {
    try {
      setError(null);

      // Eerst permissie vragen
      await navigator.mediaDevices.getUserMedia({ video: true });

      // Zoek camera met torch
      const result = await findCameraWithTorch();

      if (result) {
        streamRef.current = result.stream;
        trackRef.current = result.track;
        setTorchAvailable(true);
        if (videoRef.current) {
          videoRef.current.srcObject = result.stream;
        }
      } else {
        // Fallback: gewone camera zonder torch
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' }
        });
        streamRef.current = stream;
        trackRef.current = stream.getVideoTracks()[0];
        setTorchAvailable(false);
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }
    } catch (err) {
      setError('Geen toegang tot camera. Gebruik de foto upload optie.');
    }
  }, []);

  // Stop camera
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
      trackRef.current = null;
    }
    setTorchOn(false);
    setTorchAvailable(false);
  }, []);

  // Toggle torch
  const toggleTorch = async () => {
    if (!trackRef.current) return;

    try {
      const newState = !torchOn;
      await trackRef.current.applyConstraints({
        advanced: [{ torch: newState } as any]
      });
      setTorchOn(newState);
    } catch (err) {
      console.error('Torch error:', err);
    }
  };

  // Auto-start camera when component mounts
  useEffect(() => {
    startCamera();
  }, [startCamera]);

  // Cleanup resources when component unmounts
  useEffect(() => {
    return () => {
      // Stop camera
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
        trackRef.current = null;
      }

      // Terminate Tesseract worker
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }

      // Clear canvas
      if (canvasRef.current) {
        const ctx = canvasRef.current.getContext('2d');
        if (ctx) {
          ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        }
      }

      // Revoke object URLs to prevent memory leaks
      if (preview && preview.startsWith('blob:')) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [preview]);

  // Capture foto van camera
  const captureFromCamera = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(video, 0, 0);
      const imageData = canvas.toDataURL('image/png');
      setPreview(imageData);
      stopCamera();
      processImage(imageData);
    }
  }, [stopCamera]);

  // Process image met Tesseract OCR
  const processImage = async (imageData: string) => {
    setScanning(true);
    setProgress(0);
    setError(null);
    setScannedText(null);

    try {
      // Create or reuse worker
      if (!workerRef.current) {
        workerRef.current = await Tesseract.createWorker('nld+eng', undefined, {
          logger: (m: LoggerMessage) => {
            if (m.status === 'recognizing text') {
              setProgress(Math.round(m.progress * 100));
            }
          }
        });
      }

      const result = await workerRef.current.recognize(imageData);

      const text = result.data.text.trim();
      if (text) {
        setScannedText(text);
      } else {
        setError('Geen tekst gevonden. Probeer een duidelijkere foto.');
      }
    } catch (err) {
      setError('Fout bij tekstherkenning. Probeer opnieuw.');
      console.error('OCR error:', err);
    } finally {
      setScanning(false);
    }
  };

  // Gebruik gescande tekst
  const selectText = (text: string) => {
    onTextScanned(text);
    handleClose();
  };

  // Sluit scanner
  const handleClose = () => {
    stopCamera();
    
    // Terminate worker
    if (workerRef.current) {
      workerRef.current.terminate();
      workerRef.current = null;
    }
    
    // Clear canvas
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      }
    }
    
    // Revoke preview URL
    if (preview && preview.startsWith('blob:')) {
      URL.revokeObjectURL(preview);
    }
    
    onClose();
  };

  // Reset voor nieuwe scan
  const resetScan = () => {
    // Revoke previous preview URL
    if (preview && preview.startsWith('blob:')) {
      URL.revokeObjectURL(preview);
    }
    
    // Clear canvas
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      }
    }
    
    setPreview(null);
    setScannedText(null);
    setError(null);
    setProgress(0);
    startCamera();
  };

  return (
    <div className="ocr-scanner-overlay">
      <div className="ocr-scanner-modal">
        <div className="ocr-scanner-header">
          <h2>Tekst Scanner</h2>
          <button className="close-btn" onClick={handleClose}>&times;</button>
        </div>

        <div className="ocr-scanner-body">
          {!preview ? (
            <>
              {/* Camera preview */}
              <div className="camera-container">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                />
                <canvas ref={canvasRef} style={{ display: 'none' }} />
              </div>

              {/* Camera controls */}
              <div className="scanner-controls">
                {torchAvailable && (
                  <button
                    className={`btn ${torchOn ? 'btn-warning' : 'btn-secondary'}`}
                    onClick={toggleTorch}
                  >
                    {torchOn ? 'Lamp UIT' : 'Lamp AAN'}
                  </button>
                )}
                <button className="btn btn-success" onClick={captureFromCamera}>
                  Maak Foto
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Preview van gemaakte/geüploade foto */}
              <div className="preview-container">
                <img src={preview} alt="Preview" />
              </div>

              {/* Scanning progress */}
              {scanning && (
                <div className="scanning-progress">
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${progress}%` }} />
                  </div>
                  <span>Tekst herkennen... {progress}%</span>
                </div>
              )}

              {/* Scanned text results */}
              {scannedText && (
                <div className="scanned-results">
                  <h3>Gevonden tekst:</h3>
                  <div className="text-results">
                    {scannedText.split('\n').filter(line => line.trim()).map((line, index) => (
                      <div key={index} className="text-line" onClick={() => selectText(line)}>
                        <span>{line}</span>
                        <button className="btn btn-sm btn-success">Gebruik</button>
                      </div>
                    ))}
                  </div>
                  <button className="btn btn-primary use-all-btn" onClick={() => selectText(scannedText)}>
                    Gebruik alle tekst
                  </button>
                </div>
              )}

              {/* Error message */}
              {error && <div className="error-message">{error}</div>}

              {/* Reset button */}
              <div className="scanner-controls">
                <button className="btn btn-secondary" onClick={resetScan}>
                  Nieuwe Scan
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OCRScanner;
